import Base64 from 'crypto-js/enc-base64';
import hmacSHA256 from 'crypto-js/hmac-sha256';

import { PKG } from '../../analytics/analyticsConsts';
import { RootState, store } from '../../app/store';
import { ANALYTICS } from '../../firebase/firebase';
import { ApiRequest, ApiResponse, emptyApiResponse } from '../../types/Api';
import { getLanguageFromCookie } from '../../utils/functionUtils';
import { getBaseUrl, getDeviceID } from './getRequestMetadata';
import {
  HEADERS,
  Method,
  PLATFORM,
  RequestType,
  ResponseCode,
} from './httpConsts';

export function getApiKey(state: RootState) {
  return (
    state.apiScreenScreenSelector?.apikey ||
    store.getState().apiScreenScreenSelector.apikey
  );
}

interface GetSignatureParams {
  path: string;
  state: any;
  body?: any;
}

// note: path is the url path, not the full url
export function getSignature({
  path,
  state,
  body,
}: GetSignatureParams): string {
  const apiKey = getApiKey(state);

  if (apiKey) {
    let hashVal = `:${path}`;
    if (body && Object.keys(body).length > 0) {
      hashVal = `${JSON.stringify(body)}:${path}`;
    }
    return Base64.stringify(hmacSHA256(hashVal, apiKey));
  }
  return '';
}

export const generateHeaders = (
  state: any,
  body: object | undefined,
  requestType: RequestType,
  url: string
): HeadersInit => {
  const parse_url = new URL(url);
  const path = parse_url.pathname;

  const getSignatureParams = {
    path,
    state,
    body: body,
  };

  const signature = getSignature(getSignatureParams);

  switch (requestType) {
    case RequestType.EXTERNAL:
      return {};
    case RequestType.INTERNAL: {
      const headers: HeadersInit = {
        [HEADERS.signature]: signature,
        [HEADERS.device]: getDeviceID(state, store.dispatch),
        [HEADERS.language]: getLanguageFromCookie(),
        [HEADERS.token]: store.getState().consumerInfoSelector.auth.token || '',
        [HEADERS.platform]: PLATFORM,
        [HEADERS.sessionId]: store.getState().apiScreenScreenSelector.sessionID,
        [HEADERS.telemetryID]:
          store.getState().apiScreenScreenSelector.telemetryID || '',
      };

      const clientSecret =
        store.getState().apiScreenScreenSelector.clientSecret;
      if (clientSecret && !clientSecret.startsWith('demo_')) {
        headers[HEADERS.clientSecret] = clientSecret;
      }

      return headers;
    }
    default:
      ANALYTICS.funcError(PKG, 'Invalid request type', 'httpRequest');
      return {} as HeadersInit;
  }
};

/**
 * @deprecated - not compatible with react query, use {@link httpRequestV2} instead
 *
 */
export const httpRequest = async (req: ApiRequest): Promise<ApiResponse> => {
  if (!req.config.method) {
    ANALYTICS.funcError(PKG, 'Invalid request method', 'httpRequest');
    throw new Error('Invalid request method');
  }

  let retryAttempts = 0;
  let resp: ApiResponse = emptyApiResponse();

  const baseUrl = req.config.baseUrl;

  if (baseUrl === '' || !baseUrl) {
    ANALYTICS.funcError(PKG, 'No base url', 'httpRequest');
    return resp;
  }

  const urlSuffix = req.urlSuffix || '';

  const url: string = `${getBaseUrl(
    store,
    req.config.type
  )}${baseUrl}${urlSuffix}`;

  const generatedHeaders = generateHeaders(
    store.getState(),
    req.body,
    req.config.type,
    url
  ) as HeadersInit;

  while (retryAttempts < req.config.retryCount) {
    try {
      const res = await fetch(url, {
        headers: generatedHeaders,
        method: req.config.method,
        keepalive: req.config.keepAlive,
        body: JSON.stringify(req.body),
      });

      return {
        status: res.status,
        message: res.statusText,
        data: {
          headers: res.headers,
          body: req.config.image
            ? URL.createObjectURL(await res.blob())
            : res.status !== ResponseCode.NO_CONTENT
              ? await res.json()
              : undefined,
        },
      };
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
    } catch (error) {
      retryAttempts++;
      resp = emptyApiResponse();
    }
  }

  const code = resp.status;
  if (code === ResponseCode.HTTP_ERROR) {
    ANALYTICS.funcError(
      PKG,
      'httpRequest request failed after all retries',
      req.config.baseUrl || 'no base url'
    );
  }

  return resp;
};

export interface ApiRequestV2 {
  path: string;
  method: Method;
  headers?: HeadersInit;
  body?: object;
}

/**
 * wrapper for http requests, compatible with react query. currently only works with internal api's
 */
export const httpRequestV2 = async (req: ApiRequestV2): Promise<any> => {
  const url: string = `${getBaseUrl(store, RequestType.INTERNAL)}${req.path}`;
  const generatedHeaders = generateHeaders(
    store.getState(),
    req.body,
    RequestType.INTERNAL,
    url
  ) as HeadersInit;

  const headers = new Headers(generatedHeaders);
  if (req.headers) {
    new Headers(req.headers).forEach((value, key) => {
      headers.set(key, value); // overrides generatedHeaders
    });
  }

  const res = await fetch(url, {
    headers,
    method: req.method,
    keepalive: false,
    body: JSON.stringify(req.body),
  });

  if (res.status === 204) {
    return { success: true };
  }

  const data = await res.json();
  if (!res.ok) {
    throw data;
  }
  return data;
};
